import { StatusIcon } from "@minket/brand/icons/status";
import type { statusObject } from "@minket/core";
import { StatusBadge } from "@minket/ui/components/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { cn } from "@minket/ui/lib/utils";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { FailoverDiagram } from "@/components/ops/failover";
import { count, plural, stamp } from "@/components/ops/format";
import { RegionPlate, regionState } from "@/components/ops/region";
import { When } from "@/components/ops/when";
import { FadeText } from "@/components/page/fade-text";
import { GhostSections } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { OrgScope } from "@/components/page/scope";
import { Section, Sheet } from "@/components/page/section";
import { Notice, QueryView } from "@/components/page/states";
import { endpoints, type Wire } from "@/lib/endpoints";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/status")({
  component: StatusPage,
});

/**
 * The operator status view (B10): what the platform last recorded about itself, never a live
 * call. The two SES regions as plates and the failover between them drawn as a line diagram with
 * their state; projects paused by their kill switch; for platform administrators the health
 * checks, every queue and every maintenance job's last run.
 */
type Status = Wire<typeof statusObject>;
type Platform = NonNullable<Status["platform"]>;

const sections = [
  { title: "Regions", description: "SES in each region of the account.", wide: true },
  { title: "Failover", description: "Where mail goes when a region refuses it.", wide: true },
  { title: "Paused projects", description: "Projects whose kill switch is on." },
];

const link =
  "rounded-sm text-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none";

function StatusPage() {
  const { organization } = useWorkspace();
  const orgId = organization?.organization_id ?? "";
  const query = useQuery({
    ...apiQuery(endpoints.status, { orgId }),
    enabled: orgId !== "",
    refetchInterval: 60_000,
  });
  return (
    <Page title="Status" description={query.data ? <Verdict status={query.data} /> : undefined}>
      <OrgScope
        will="SES in each region, failover and paused projects appear here once it sends."
        icon={<StatusIcon />}
        preview={() => <GhostSections sections={sections} />}
      >
        {() => <StatusBody query={query} />}
      </OrgScope>
    </Page>
  );
}

/** The sentence under the title: how the primary region stands, failover, and open alerts. */
function Verdict({ status }: { status: Status }) {
  const primary = status.ses.find((region) => region.role === "primary");
  const secondary = status.ses.find((region) => region.role === "secondary");
  const word = primary ? regionState(primary).word.toLowerCase() : "not read yet";
  return (
    <>
      {primary ? `${primary.region} is ${word}.` : "SES has not been read yet."}{" "}
      {status.failover.active
        ? `${secondary?.region ?? "The secondary region"} carried ${plural(status.failover.sends_24h, "email")} in the last 24 hours. `
        : ""}
      {status.open_alerts > 0 ? (
        <Link to="/alerts" className={link}>
          {plural(status.open_alerts, "alert")} {status.open_alerts === 1 ? "is" : "are"} open.
        </Link>
      ) : (
        "No alert is open."
      )}
    </>
  );
}

function StatusBody({ query }: { query: UseQueryResult<Status> }) {
  return (
    <QueryView
      query={query}
      noun="the platform status"
      preview={() => <GhostSections sections={sections} />}
    >
      {(status) => (
        <Sheet>
          <Section
            wide
            title="Regions"
            description={
              status.platform
                ? "SES in the platform's account, which every organization shares, read every hour. Its quota counts every organization's mail."
                : "SES in the platform's account, which every organization shares, read every hour. Platform administrators also see the day's quota."
            }
          >
            <div className="@container min-w-0">
              <div className="grid min-w-0 gap-4 @min-[40rem]:grid-cols-2">
                {status.ses.map((region) => (
                  <RegionPlate key={region.region} region={region} />
                ))}
              </div>
            </div>
          </Section>
          <Section
            wide
            title="Failover"
            description={
              status.failover.active && status.failover.last_at
                ? `The secondary region carried ${plural(status.failover.sends_24h, "email")} in the last 24 hours, the last at ${stamp(status.failover.last_at)} UTC.`
                : "No mail left through the secondary region in the last 24 hours."
            }
          >
            <FailoverDiagram status={status} />
          </Section>
          <PausedProjects status={status} />
          {status.platform ? (
            <PlatformSections platform={status.platform} />
          ) : (
            <Notice>
              The health checks, queues and maintenance jobs are shown to platform administrators.
            </Notice>
          )}
        </Sheet>
      )}
    </QueryView>
  );
}

function Lamp({
  tone,
  children,
}: {
  tone: "success" | "warning" | "destructive" | "neutral";
  children: ReactNode;
}) {
  return <StatusBadge tone={tone}>{children}</StatusBadge>;
}

function PausedProjects({ status }: { status: Status }) {
  return (
    <Section
      title="Paused projects"
      description="A project's kill switch holds its live mail until someone clears it in the project's settings."
    >
      {status.paused_projects.length === 0 ? (
        <Notice tone="success">Every project is sending.</Notice>
      ) : (
        <ul className="grid min-w-0 gap-4">
          {status.paused_projects.map((project) => (
            <li
              key={project.project_id}
              className="grid min-w-0 gap-1 border-l-[3px] border-destructive py-0.5 pl-4"
            >
              <span className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-semibold">{project.name}</span>
                <Lamp tone="destructive">
                  {project.kill_switch === "manual" ? "Paused by hand" : "Paused automatically"}
                </Lamp>
                {project.since ? (
                  <span className="text-[13px] whitespace-nowrap text-muted-foreground">
                    since <When value={project.since} /> UTC
                  </span>
                ) : null}
              </span>
              {project.reason ? (
                <span className="text-[13.5px] text-muted-foreground wrap-break-word">
                  {project.reason}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

const jobStates = {
  succeeded: ["success", "Succeeded"],
  failed: ["destructive", "Failed"],
  running: ["neutral", "Running"],
} as const;

const numeric = "type-data text-right text-[13px] whitespace-nowrap";

function PlatformSections({ platform }: { platform: Platform }) {
  return (
    <>
      <Section
        wide
        title="Health checks"
        description={platform.probed_at ? `Checked ${stamp(platform.probed_at)} UTC.` : undefined}
      >
        {platform.probes.length === 0 ? (
          <Notice>No health check has run yet.</Notice>
        ) : (
          <Table aria-label="Health checks">
            <TableHeader>
              <TableRow>
                <TableHead>Host</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Status</TableHead>
                <TableHead className="text-right">Latency</TableHead>
                <TableHead>Answer</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {platform.probes.map((probe) => (
                <TableRow key={probe.name} className="hover:bg-transparent">
                  <TableCell className="type-data whitespace-nowrap">{probe.name}</TableCell>
                  <TableCell>
                    <Lamp tone={probe.ok ? "success" : "destructive"}>
                      {probe.ok ? "Answering" : "Failing"}
                    </Lamp>
                  </TableCell>
                  <TableCell className={numeric}>{probe.status ?? "None"}</TableCell>
                  <TableCell className={numeric}>
                    {probe.latency_ms === null ? "None" : `${count(probe.latency_ms)} ms`}
                  </TableCell>
                  <TableCell className="w-full max-w-0 min-w-40">
                    {probe.error ? (
                      <FadeText className="type-data text-[13px] text-muted-foreground">
                        {probe.error}
                      </FadeText>
                    ) : (
                      <span className="text-muted-foreground">ok: true</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      <Section wide title="Queues">
        {platform.queues.length === 0 ? (
          <Notice>Queue depths have not been read yet.</Notice>
        ) : (
          <Table aria-label="Queue depths">
            <TableHeader>
              <TableRow>
                <TableHead>Queue</TableHead>
                <TableHead className="text-right">Waiting</TableHead>
                <TableHead className="text-right">In flight</TableHead>
                <TableHead className="text-right">Delayed</TableHead>
                <TableHead>Dead letter</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {platform.queues.map((queue) => (
                <TableRow
                  key={queue.name}
                  className={cn(
                    "hover:bg-transparent",
                    (queue.dead_letter ?? 0) > 0 &&
                      "[&>td:first-child]:shadow-[inset_3px_0_0_var(--destructive)]",
                  )}
                >
                  <TableCell className="type-data whitespace-nowrap">{queue.name}</TableCell>
                  <TableCell className={numeric}>{count(queue.visible)}</TableCell>
                  <TableCell className={numeric}>{count(queue.in_flight)}</TableCell>
                  <TableCell className={numeric}>{count(queue.delayed)}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {queue.dead_letter === null ? (
                      <span className="text-muted-foreground">Not read</span>
                    ) : queue.dead_letter > 0 ? (
                      <Lamp tone="destructive">{count(queue.dead_letter)} parked</Lamp>
                    ) : (
                      <Lamp tone="success">Empty</Lamp>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      <Section wide title="Jobs">
        <Table aria-label="Maintenance jobs">
          <TableHeader>
            <TableRow>
              <TableHead>Job</TableHead>
              <TableHead>State</TableHead>
              <TableHead className="text-right">Last run, UTC</TableHead>
              <TableHead className="text-right">Last success, UTC</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {platform.jobs.map((job) => {
              const state = job.status ? jobStates[job.status] : null;
              return (
                <TableRow
                  key={job.kind}
                  className={cn(
                    "hover:bg-transparent",
                    job.status === "failed" &&
                      "[&>td:first-child]:shadow-[inset_3px_0_0_var(--destructive)]",
                  )}
                >
                  <TableCell className="type-data whitespace-nowrap">{job.kind}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {state ? (
                      <Lamp tone={state[0]}>{state[1]}</Lamp>
                    ) : (
                      <span className="text-muted-foreground">Never run</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <When value={job.started_at} empty="Never" />
                  </TableCell>
                  <TableCell className="text-right">
                    <When value={job.last_success_at} empty="Never" />
                  </TableCell>
                  <TableCell className="w-full max-w-0 min-w-40">
                    {job.error ? (
                      <FadeText className="type-data text-[13px] text-muted-foreground">
                        {job.error}
                      </FadeText>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Section>
    </>
  );
}
